import { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '~/hooks/useAuth/useAuth';
import authService from '~/services/authService/auth.service';
import useUserContext from '~/hooks/useUserContext';

export interface User {
  id: number;
  email: string;
  name: string;
  roles: string[];
  permissions: string[];
}

export const useManagementAuth = () => {
  const router = useRouter();
  const baseAuth = useAuth();
  const {
    user: contextUser,
    isAuthenticated,
    loading: userContextLoading,
    logout: userLogout,
  } = useUserContext();
  const [isInitialized, setIsInitialized] = useState(false);

  // Fetch user permissions
  const {
    data: userPermissions,
    isLoading: isPermissionsLoading,
    error: permissionsError,
    refetch: refetchPermissions,
  } = useQuery({
    queryKey: ['userPermissions'],
    queryFn: async () => {
      // Only fetch permissions if the user is authenticated
      if (!contextUser?.id) {
        return { roles: [], permissions: [] };
      }

      try {
        // Fetch user permissions from the management API
        const response = await authService.getUserPermissions();

        // Validate the response
        if (
          !response ||
          !Array.isArray(response.permissions) ||
          !Array.isArray(response.roles)
        ) {
          console.error('Invalid permissions response:', response);
          return { roles: [], permissions: [] };
        }

        // Filter out any null or undefined values
        return {
          roles: response.roles.filter(Boolean),
          permissions: response.permissions.filter(Boolean),
        };
      } catch (error) {
        console.error('Error fetching user permissions:', error);
        return { roles: [], permissions: [] };
      }
    },
    enabled: isInitialized && isAuthenticated,
  });

  useEffect(() => {
    // Initialize after component mount to avoid SSR issues
    setIsInitialized(true);
  }, []);

  // Refetch permissions when the user changes
  useEffect(() => {
    if (contextUser?.id && isInitialized) {
      refetchPermissions();
    }
  }, [contextUser?.id, isInitialized, refetchPermissions]);

  const logout = async () => {
    try {
      await authService.logout();
      userLogout();
      router.push('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const hasPermission = (permission: string): boolean => {
    if (!contextUser || !userPermissions || !permission) return false;

    // Ensure permissions is an array and contains valid values
    const permissions = userPermissions.permissions || [];
    if (!Array.isArray(permissions)) return false;

    return permissions.includes(permission);
  };

  const hasRole = (role: string): boolean => {
    if (!contextUser || !userPermissions || !role) return false;

    // Ensure roles is an array and contains valid values
    const roles = userPermissions.roles || [];
    if (!Array.isArray(roles)) return false;

    return roles.includes(role);
  };

  const isAdmin = (): boolean => {
    return hasRole('admin');
  };

  // Combine user data from context with permissions
  const user = contextUser
    ? {
        ...contextUser,
        roles: userPermissions?.roles || [],
        permissions: userPermissions?.permissions || [],
      }
    : null;

  return {
    ...baseAuth,
    user,
    isLoading: baseAuth.isLoading || userContextLoading || isPermissionsLoading,
    error: permissionsError,
    logout,
    hasPermission,
    hasRole,
    isAdmin,
    isAuthenticated: !!contextUser,
    refetchPermissions,
  };
};

export default useManagementAuth;
